import * as StateLib from "@/Utils/StateLib";
import React, { Fragment, useRef } from "react";
import {
  ToolIcon,
  Menu,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  ColorTrigger
} from "@theorgchartdev/storybook";
import CompositeColorPicker from "../../../Blocks/Composites/CompositeColorPicker/CompositeColorPicker";
import {
  BuildBoxShapeOptions,
  BuildBorderWeightOptions,
  BuildBorderStyleOptions,
  BuildBorderDashOptions
} from "../ToolbarOptions";
import { BoxFlagComposite } from "./BoxFlagComposite";
import { getHoverTipContent } from "@/Utils/Misc";
import { encodeColorObject } from "@/appComponents/Blocks/Composites/CompositeColorPicker/ColorPicker.utils";
import { appstr } from "@coreapp/utils/appstr";
import { enumUpdateProperty, enumFillMode } from "@coreapp/constants";
import { QColorPickerResult } from "@coreapp/CoreApp/QColorPickerResult";
import { Globals } from "@coreapp/CoreApp/Globals";
import { editModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

const dividerClass = "ocn-toolbar-horizontal-divider";

const BoxFormatToolsComposite = () => {
  const inBackgroundEditMode = Globals._uiConfig.InBackgroundEditorMode();
  const inBoxLayoutEditorMode = Globals._uiConfig.InBoxLayoutEditorMode();
  const modeTestId = (suffix, editSuffix = suffix) =>
    inBoxLayoutEditorMode
      ? `box-layout-editor-toolbar-${suffix}`
      : inBackgroundEditMode
        ? `background-editor-toolbar-${suffix}`
        : editModeTestId(editSuffix);
  const boxShapeOpenCtrlRef = useRef(null);
  const boxFillColorOpenCtrlRef = useRef(null);
  const boxBorderColorOpenCtrlRef = useRef(null);
  const borderWeightOpenCtrlRef = useRef(null);
  const borderStyleOpenCtrlRef = useRef(null);
  const borderDashOpenCtrlRef = useRef(null);

  let wsMgr = StateLib.GetWSMgr();

  let currentBoxPropsToggled = [];
  let boxShape;
  let borderColor = "#ffffff";
  let boxFillColor = "#ffffff";
  let boxFillGradientColor = null;
  let borderWeight;
  let borderStyle;
  let dashStyle;
  let isPicture = false;
  let firstSelectedTextBox = null;
  if (wsMgr) {
    let boxShadowOn = wsMgr.SurveySelection(enumUpdateProperty.boxShadow);
    if (boxShadowOn) currentBoxPropsToggled.push("boxshadow");
    firstSelectedTextBox = wsMgr.GetFirstSelectedBox(true);
    borderWeight = wsMgr.SurveySelection(enumUpdateProperty.boxBorderWeight);
    borderStyle = wsMgr.SurveySelection(enumUpdateProperty.boxBorderStyle);
    dashStyle = wsMgr.SurveySelection(enumUpdateProperty.boxDashStyle);
    boxShape = wsMgr.SurveySelection(enumUpdateProperty.boxShapeType);
    let borderColorCP = wsMgr.SurveySelection(enumUpdateProperty.boxBorderColor);
    if (borderColorCP) borderColor = borderColorCP.fSelectedColor;
    let boxFillColorCP = wsMgr.SurveySelection(enumUpdateProperty.boxFillColor);
    if (boxFillColorCP) {
      switch (boxFillColorCP.fFillMode) {
        case enumFillMode.noFill:
          boxFillColor = null;
          break;
        case enumFillMode.linearGradientDown:
          boxFillGradientColor = boxFillColorCP.fSelectedColor2;
        case enumFillMode.solidFill:
          boxFillColor = boxFillColorCP.fSelectedColor;
          break;
      }
    }
    if (firstSelectedTextBox) isPicture = firstSelectedTextBox.IsStaticPicture(); // && !roMode
  }

  const boxShapeTrigger = (
    <ToolIcon
      name={"vector-square-regular"}
      title={appstr("Box Shape")}
      tooltipProps={{
        ...getHoverTipContent("boxshape"),
        referenceOpenCtrlRef: boxShapeOpenCtrlRef
      }}
    />
  );
  const boxShapeOptions = BuildBoxShapeOptions(UpdateBoxShape);

  const borderWeightTrigger = (
    <ToolIcon
      name={"lines-regular"}
      title={appstr("Border Weight")}
      tooltipProps={{
        ...getHoverTipContent("boxweight"),
        referenceOpenCtrlRef: borderWeightOpenCtrlRef
      }}
    />
  );
  const borderWeightOptions = BuildBorderWeightOptions(UpdateBorderWeight);

  const borderStyleTrigger = (
    <ToolIcon
      name={"border-double"}
      title={appstr("Border type")}
      tooltipProps={{
        ...getHoverTipContent("boxborder"),
        referenceOpenCtrlRef: borderStyleOpenCtrlRef
      }}
    />
  );
  const borderStyleOptions = BuildBorderStyleOptions(UpdateBorderStyle);

  const borderDashTrigger = (
    <ToolIcon
      name={"square-dashed"}
      title={appstr("Border dash")}
      tooltipProps={{
        ...getHoverTipContent("boxborder"),
        referenceOpenCtrlRef: borderDashOpenCtrlRef
      }}
    />
  );
  const borderDashOptions = BuildBorderDashOptions(UpdateBorderDash);

  return (
    <Fragment>
      <Menu
        openCtrlRef={boxShapeOpenCtrlRef}
        dataTestId={modeTestId("box-shape-menu")}
        trigger={boxShapeTrigger}
        listProps={{ grid: true, gridCols: 2 }}
        options={boxShapeOptions}
        value={boxShape}
      />
      <CompositeColorPicker
        openCtrlRef={boxFillColorOpenCtrlRef}
        dataTestId={modeTestId("box-fill-color")}
        trigger={
          <ColorTrigger
            iconName="fill-drip"
            size={"small"}
            tooltipProps={{
              ...getHoverTipContent("boxfillcolor"),
              referenceOpenCtrlRef: boxFillColorOpenCtrlRef
            }}
          />
        }
        showGradient
        onChange={UpdateBoxFill}
        currentColor={encodeColorObject(boxFillColor, boxFillGradientColor)}
      />
      <ToggleButtonGroup onChange={UpdateBoxShadow} value={currentBoxPropsToggled}>
        <ToggleButton
          id={"box-shadow-btn"}
          data-testid={modeTestId("box-shadow-btn", "box-shadow-toggle")}
          icon={"drop-shadow_"}
          value={"boxshadow"}
          title={appstr("Box shadow")}
          tooltipProps={getHoverTipContent("boxshadow")}
        />
      </ToggleButtonGroup>
      <div className={dividerClass}>
        <Divider vertical={true} />
      </div>
      <CompositeColorPicker
        openCtrlRef={boxBorderColorOpenCtrlRef}
        dataTestId={modeTestId("box-border-color")}
        trigger={
          <ColorTrigger
            iconName="pen"
            size={"small"}
            tooltipProps={{
              ...getHoverTipContent("boxbordercolor"),
              referenceOpenCtrlRef: boxBorderColorOpenCtrlRef
            }}
          />
        }
        onChange={UpdateBoxBorderColor}
        currentColor={borderColor}
        showNoFill={false}
      />
      <BoxFlagComposite
        dataTestId={
          inBoxLayoutEditorMode
            ? "box-layout-editor-toolbar-box-flag"
            : inBackgroundEditMode
              ? "background-editor-toolbar-box-flag"
              : undefined
        }
      />
      <Menu
        openCtrlRef={borderWeightOpenCtrlRef}
        dataTestId={modeTestId("border-weight-menu")}
        trigger={borderWeightTrigger}
        disabled={false}
        options={borderWeightOptions}
        value={borderWeight}
      />
      <Menu
        openCtrlRef={borderStyleOpenCtrlRef}
        dataTestId={modeTestId("border-style-menu")}
        trigger={borderStyleTrigger}
        disabled={false}
        options={borderStyleOptions}
        value={borderStyle}
      />
      <Menu
        openCtrlRef={borderDashOpenCtrlRef}
        dataTestId={modeTestId("border-dash-menu")}
        trigger={borderDashTrigger}
        disabled={false}
        options={borderDashOptions}
        value={dashStyle}
      />
    </Fragment>
  );
};

const UpdateBoxShadow = selectedBoxProps => {
  StateLib.UpdateBoxProperty(enumUpdateProperty.boxShadow, selectedBoxProps.includes("boxshadow"));
};

const UpdateBoxFill = selectedColor => {
  const color = new QColorPickerResult();
  color.fFillMode = enumFillMode.noFill;
  color.fSelectedColor = selectedColor;

  if (Array.isArray(selectedColor)) {
    color.fFillMode = enumFillMode.linearGradientDown;
    color.fSelectedColor = selectedColor[0];
    color.fSelectedColor2 = selectedColor[1];
  } else if (selectedColor) {
    color.fFillMode = enumFillMode.solidFill;
  }

  StateLib.UpdateBoxProperty(enumUpdateProperty.boxFillColor, color);
};

const UpdateBoxBorderColor = selectedColor => {
  let color = new QColorPickerResult();
  color.fSelectedColor = selectedColor;
  color.fFillMode = selectedColor ? enumFillMode.solidFill : enumFillMode.noFill;
  StateLib.UpdateBoxProperty(enumUpdateProperty.boxBorderColor, color);
};

const UpdateBoxShape = target => {
  const newShapeType = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.boxShapeType, newShapeType);
};

const UpdateBorderWeight = target => {
  const newBorderWeight = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.boxBorderWeight, newBorderWeight);
};

const UpdateBorderStyle = target => {
  const newBorderWeight = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.boxBorderStyle, newBorderWeight);
};

const UpdateBorderDash = target => {
  const newBorderWeight = target.value;
  StateLib.UpdateBoxProperty(enumUpdateProperty.boxDashStyle, newBorderWeight);
};

export default BoxFormatToolsComposite;
